import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from '@components/ui/Menu/Menu';
import { Avatar } from '@components/ui/Avatar/Avatar';
import { requestTour, tourIdForPath } from '@components/PageTour/tourRegistry';
import { useAuth } from '@contexts/useAuth';
import { selectSiteId } from '@store/selectors/site.selectors';
import { EyeIcon, LightbulbIcon } from '@utils/icons';
import { setPageTitlesVisible, usePageTitlesVisible } from '@hooks/usePageTitlesVisible';
import styles from './styles.module.css';

const LANG_LABEL: Record<string, string> = {
  en: 'English (US)',
  ja: '日本語',
};

function UserIconCircle() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.5-3.5 4.5-5 7-5s5.5 1.5 7 5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
      <path d="M16 8l4 4-4 4" />
      <path d="M20 12H10" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5.5c0 4.3-2.9 7.7-7 9.5-4.1-1.8-7-5.2-7-9.5V6z" />
      <path d="M9.5 12l1.8 1.8 3.4-3.6" />
    </svg>
  );
}

function BellThinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export function UserMenu({ collapsed = false, showMeta = true, popupSide = 'right' }: { collapsed?: boolean; showMeta?: boolean; popupSide?: 'right' | 'bottom' } = {}) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Alert settings are per project: the working site's, or — before one is
  // chosen — the site picker. Every role has its own.
  const siteId = useSelector(selectSiteId);
  const alertSettingsPath = siteId ? `/settings/alerts/${encodeURIComponent(siteId)}` : '/settings/alerts';
  const tourId = tourIdForPath(pathname);
  const currentLng = i18n.language.startsWith('ja') ? 'ja' : 'en';
  const chooseLang = (lng: 'en' | 'ja') => void i18n.changeLanguage(lng);

  const titlesVisible = usePageTitlesVisible();
  const displayName = user?.username ?? '';
  const hideMeta = collapsed || !showMeta;

  return (
    <Menu.Root>
      <Menu.Trigger
        className={`${styles.trigger} ${hideMeta ? styles.triggerCollapsed : ''}`}
        title={displayName}
      >
        <Avatar name={displayName || '??'} size={32} tone="solid" />
        {!hideMeta && (
          <span className={styles.meta}>
            <span className={styles.name}>{displayName}</span>
            <span className={styles.org}>{user?.orgId ?? ''}</span>
          </span>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side={popupSide} align="end" sideOffset={8}>
          <Menu.Popup className={styles.popup}>
            <header className={styles.popoverHeader}>
              <span className={styles.avatarLg}><UserIconCircle /></span>
              <div className={styles.identity}>
                <div className={styles.identityRow}>
                  <span className={styles.identityName}>{displayName}</span>
                </div>
                <div className={styles.identityEmail}>{user?.orgId ?? ''}</div>
              </div>
            </header>

            <Menu.Item onClick={() => navigate(alertSettingsPath)}>
              <span className={styles.menuIcon}><BellThinIcon /></span>
              <span className={styles.menuLabel}>{t('userMenu.alertsSettings')}</span>
            </Menu.Item>

            {/* Account security — two-factor status / enroll / disable. A
                company-account (SSO) session has neither: its company owns both. */}
            {user?.authMethod !== 'sso' && (
              <Menu.Item onClick={() => navigate('/settings/security')}>
                <span className={styles.menuIcon}><ShieldIcon /></span>
                <span className={styles.menuLabel}>{t('userMenu.security')}</span>
              </Menu.Item>
            )}

            <Menu.SubmenuRoot>
              <Menu.SubmenuTrigger>
                <span className={styles.menuIcon}><GlobeIcon /></span>
                <span className={styles.menuLabel}>{t('userMenu.languages')}</span>
                <span className={styles.langSelect}>
                  {LANG_LABEL[currentLng]}
                  <ChevronRightIcon />
                </span>
              </Menu.SubmenuTrigger>
              <Menu.Portal>
                <Menu.Positioner side="right" align="start" sideOffset={6}>
                  <Menu.Popup>
                    <Menu.Item
                      className={currentLng === 'en' ? styles.langOptionActive : undefined}
                      onClick={() => chooseLang('en')}
                    >
                      English (US)
                    </Menu.Item>
                    <Menu.Item
                      className={currentLng === 'ja' ? styles.langOptionActive : undefined}
                      onClick={() => chooseLang('ja')}
                    >
                      日本語
                    </Menu.Item>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.SubmenuRoot>

            {/* A display preference: the title and subtitle at the top of every page. */}
            <Menu.CheckboxItem checked={titlesVisible} onCheckedChange={setPageTitlesVisible} closeOnClick={false}>
              <span className={styles.menuIcon}><EyeIcon size={18} /></span>
              <span className={styles.menuLabel}>{t('userMenu.pageTitles')}</span>
              <span className={styles.langSelect}>{t(titlesVisible ? 'userMenu.on' : 'userMenu.off')}</span>
            </Menu.CheckboxItem>

            {/* Replays the current page's tour. Disabled, not hidden, on a page
                without one: the item's place in the menu is the discoverable
                fact; that this page has nothing to show is the state. */}
            <Menu.Item disabled={tourId === null} onClick={tourId ? () => requestTour(tourId) : undefined}>
              <span className={styles.menuIcon}><LightbulbIcon /></span>
              <span className={styles.menuLabel}>{t('userMenu.pageTour')}</span>
            </Menu.Item>

            <Menu.Item onClick={logout}>
              <span className={styles.menuIcon}><LogoutIcon /></span>
              <span className={styles.menuLabel}>{t('userMenu.logout')}</span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
